import React, { useState, useEffect, useRef } from 'react';

interface Testimonial {
  id: number;
  name: string;
  position: string;
  company: string;
  content: string;
  rating: number;
  avatar: string;
  logo: string;
}

const Testimonials: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const sectionRef = useRef<HTMLElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const testimonials: Testimonial[] = [
    {
      id: 1,
      name: 'Martyn Charles',
      position: 'Founder',
      company: 'Aurzart',
      content: 'I’m a musician, not a techie — yet today I run my entire online store by myself! Thanks to Jagathi Solutions no-code approach and genuine guidance, I felt supported every step of the way. They’ve truly lived up to their motto: Trusted Technology Partnership.',
      rating: 5,
      avatar: 'MC',
      logo: 'TS'
    },
    {
      id: 2,
      name: 'Deepak J',
      position: 'CEO',
      company: 'EZee Infotech',
      content: 'Jagathi Solutions handled our database migration flawlessly — zero downtime, zero stress. Their technical precision and dependable support have made them our trusted long-term partner.',
      rating: 5,
      avatar: 'DJ',
      logo: 'FG'
    },
    {
      id: 3,
      name: 'Satyanarayana M',
      position: 'Director',
      company: 'Sridevi Group of Companies',
      content: 'Jagathi Solutions made our digital transition seamless — from e-commerce to accounting. Their ‘empower to grow’ approach helped us build capability, not dependency.',
      rating: 5,
      avatar: 'SM',
      logo: 'PS'
    },
    {
      id: 4,
      name: 'Founders of Dollar EH',
      position: 'Founder',
      company: 'Dollar EH Canada',
      content: 'Partnering with Jagathi Solutions transformed our retail presence. They built a custom e-commerce platform tailored for the Canadian market — complete with tax and payment integrations, real-time inventory, mobile optimization, and SEO. The result has been remarkable: wider reach across provinces and stronger customer satisfaction.',
      rating: 5,
      avatar: 'DEH',
      logo: 'EH'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Component is visible
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;

    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAutoPlaying, testimonials.length]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    
    // Resume auto-play after 10 seconds
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 10000);
  };

  const nextSlide = () => {
    setCurrentIndex(currentIndex === testimonials.length - 1 ? 0 : currentIndex + 1);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const prevSlide = () => {
    setCurrentIndex(currentIndex === 0 ? testimonials.length - 1 : currentIndex - 1);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <svg
        key={index}
        className={`w-5 h-5 ${index < rating ? 'text-yellow-400' : 'text-gray-600'}`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  return (
    <section ref={sectionRef} id="testimonials" className="py-10 dark:bg-navy text-bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold dark:text-white text-text-gray-900 mb-6">
            Client <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-light">Testimonials</span>
          </h2>
          <p className="text-xl dark:text-gray-300 text-text-gray-600 max-w-3xl mx-auto">
            Don't just take our word for it. Here's what our clients have to say about 
            working with us.
          </p>
        </div>

        {/* Testimonial Carousel */}
        <div className="relative">
          {/* Main Testimonial */}
          <div className="glass rounded-2xl p-8 md:p-12 max-w-4xl mx-auto">
            <div className="text-center">
              {/* Stars */}
              <div className="flex justify-center mb-6">
                {renderStars(testimonials[currentIndex].rating)}
              </div>

              {/* Quote */}
              <blockquote className="text-xl md:text-2xl dark:text-gray-300 text-text-gray-600 leading-relaxed mb-8 italic">
                "{testimonials[currentIndex].content}"
              </blockquote>

              {/* Client Info */}
              <div className="flex flex-col md:flex-row items-center justify-center gap-6">
                {/* Avatar */}
                <div className="w-16 h-16 bg-gradient-to-br from-accent to-accent-light rounded-full flex items-center justify-center text-white font-bold text-xl">
                  {testimonials[currentIndex].avatar}
                </div>

                {/* Client Details */}
                <div className="text-center md:text-left">
                  <h4 className="text-xl font-semibold dark:text-white text-text-gray-900 mb-1">
                    {testimonials[currentIndex].name}
                  </h4>
                  <p className="text-accent-light font-medium mb-1">
                    {testimonials[currentIndex].position}
                  </p>
                  <div className="flex items-center justify-center md:justify-start gap-2">
                    <div className="w-8 h-8 dark:bg-gray-600 text-bg-gray-300 rounded flex items-center justify-center text-xs font-bold text-white">
                      {testimonials[currentIndex].logo}
                    </div>
                    <span className="dark:text-gray-400 text-text-gray-500 text-sm">
                      {testimonials[currentIndex].company}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 glass rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all duration-300 group"
          >
            <svg className="w-6 h-6 dark:text-gray-400 text-text-gray-500 group-hover:dark:text-white group-hover:text-text-gray-900 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 glass rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all duration-300 group"
          >
            <svg className="w-6 h-6 dark:text-gray-400 text-text-gray-500 group-hover:dark:text-white group-hover:text-text-gray-900 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Dots Indicator */}
        <div className="flex justify-center mt-8 gap-2">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-accent scale-125'
                  : 'dark:bg-gray-600 text-bg-gray-300 hover:dark:bg-gray-500 hover:text-bg-gray-400'
              }`}
            />
          ))}
        </div>

        {/* Auto-play Indicator */}
        <div className="text-center mt-6">
          <div className="inline-flex items-center gap-2 text-sm dark:text-gray-400 text-text-gray-500">
            <div className={`w-2 h-2 rounded-full ${isAutoPlaying ? 'bg-green-400' : 'dark:bg-gray-600 text-bg-gray-300'}`}></div>
            <span>{isAutoPlaying ? 'Auto-playing' : 'Paused'}</span>
          </div>
        </div>        
      </div>
    </section>
  );
};

export default Testimonials;
